import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { COOKIES } from "src/app/core/models/cookie";
import { BluStorageService } from "src/app/core/services/blu-storage.service";
import { AuthenticationService } from "src/app/modules/authentication/services/authentication.service";
import { REPORT_CODE } from "../../../models/constants";
import { AdminPendingReports } from "../../../models/report";
import { AsyncReport, getAsyncReportUrl } from "../models/async-report";

@Injectable({
  providedIn: "root",
})
export class AsyncReportService {
  constructor(
    private authenticationService: AuthenticationService,
    private storageService: BluStorageService,
    private router: Router,
  ) {}

  savePendingReportInStorage(report: AsyncReport) {
    const adminId: number | undefined =
      this.authenticationService.getAuthenticatedAdminData()?.id;
    const adminsPendingReports: AdminPendingReports[] =
      this.getAdminsPendingReports();

    // Find the admin's pending reports
    const pendingReportsEntry = adminsPendingReports.find(
      (entry) => entry.adminId === adminId,
    );

    // Extract the reports array if the entry exists, otherwise initialize it to an empty array
    const pendingReports = pendingReportsEntry
      ? pendingReportsEntry.reports
      : [];

    const index = pendingReports.findIndex(
      (pendingReport) => pendingReport.code === report.code,
    );

    if (index !== -1) {
      // If the report is found, replace it with the new report
      pendingReports.splice(index, 1, report);
    } else {
      // Otherwise, add the new report to the array
      pendingReports.push(report);
    }

    // Update the storage with the new pending reports
    if (pendingReportsEntry) {
      // If the entry exists, update it in the storage
      pendingReportsEntry.reports = pendingReports;
    } else if (adminId) {
      // Otherwise, create a new entry for the admin
      adminsPendingReports.push({ adminId, reports: pendingReports });
    }

    // Save the updated adminPendingReports
    this.storageService.setLocalStorage(
      COOKIES.PENDING_ASYNC_REPORT,
      adminsPendingReports,
    );
  }

  setReportBeingGenerated(reportCode: REPORT_CODE, value: boolean) {
    if (value) {
      this.storageService.setLocalStorage(
        COOKIES.REPORT_BEING_GENERATED,
        reportCode,
      );
    } else {
      this.storageService.removeFromLocalStorage(
        COOKIES.REPORT_BEING_GENERATED,
      );
    }
  }

  /**
   * Removes a pending report from storage for the authenticated admin.
   * @param code The code of the report to remove.
   */
  removePendingReportFromStorage(code: REPORT_CODE) {
    // Get the ID of the authenticated admin
    const adminId: number | undefined =
      this.authenticationService.getAuthenticatedAdminData()?.id;

    // Get the list of pending reports from storage
    const adminsPendingReports = this.getAdminsPendingReports();

    // Iterate over each pending report
    adminsPendingReports.forEach((pendingReport) => {
      // Check if the pending report belongs to the authenticated admin
      if (pendingReport.adminId === adminId) {
        // Filter out the report with the specified code
        pendingReport.reports = pendingReport.reports.filter(
          (report) => report.code !== code,
        );
      }
    });

    // Check if there are any pending reports left
    if (adminsPendingReports.length) {
      // Save the updated list of pending reports to storage
      this.storageService.setLocalStorage(
        COOKIES.PENDING_ASYNC_REPORT,
        adminsPendingReports,
      );
    } else {
      // If there are no pending reports left, remove the storage entry
      this.storageService.removeFromLocalStorage(COOKIES.PENDING_ASYNC_REPORT);
    }
  }

  /**
   * Retrieves the list of pending async reports for the authenticated admin from storage.
   * @returns An array of AsyncReport objects.
   */
  getPendingReportsForAdmin(): AsyncReport[] {
    // Get the ID of the authenticated admin
    const adminId: number | undefined =
      this.authenticationService.getAuthenticatedAdminData()?.id;

    // Get the list of pending reports from storage or initialize it to an empty array if it doesn't exist
    const adminPendingReports: AdminPendingReports[] =
      this.storageService.getFromLocalStorage(COOKIES.PENDING_ASYNC_REPORT) ??
      [];

    // Find the entry for the authenticated admin in the list of pending reports
    const pendingReportsEntry = adminPendingReports.find(
      (report) => report.adminId === adminId,
    );

    // Return the list of pending reports for the authenticated admin or an empty array if not found
    return pendingReportsEntry ? pendingReportsEntry.reports : [];
  }

  getAdminsPendingReports(): AdminPendingReports[] {
    return (
      this.storageService.getFromLocalStorage(COOKIES.PENDING_ASYNC_REPORT) ??
      []
    );
  }

  /**
   * @returns the report being generated from storage
   */
  getReportBeingGenerated(): REPORT_CODE {
    return this.storageService.getFromLocalStorage(
      COOKIES.REPORT_BEING_GENERATED,
    );
  }

  isReportAvailableInStorage(code: REPORT_CODE): boolean {
    const pendingReports = this.getPendingReportsForAdmin();
    const report = pendingReports?.find((report) => report.code === code);
    return report ? true : false;
  }

  getPendingReportByCodeFromStorage(code: REPORT_CODE): AsyncReport | null {
    const pendingReports = this.getPendingReportsForAdmin() || [];
    const report = pendingReports.find(
      (pendingReport) => pendingReport.code === code,
    );
    return report ?? null;
  }

  redirectToReport(code: REPORT_CODE) {
    const url = getAsyncReportUrl(code);
    if (url) {
      this.router.navigate([url], { queryParams: { isCached: true } });
    }
  }
}
